/**
 * Format a date/timestamp string according to user's Settings > General preferences:
 *   - Date Format (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD)
 *   - Time Format (24h or 12h)
 *   - Timezone (IANA string, e.g. "Australia/Sydney")
 *
 * IMPORTANT: All dates displayed to users MUST use this utility to respect
 * the user's chosen format from Settings > General > Date & Time Format.
 *
 * @param {string|Date} timestamp - ISO string or Date object
 * @param {string} dateFormat - 'DD/MM/YYYY', 'MM/DD/YYYY', or 'YYYY-MM-DD'
 * @param {string} timeFormat - '24h' or '12h'
 * @param {string} [userTimezone] - IANA timezone string (e.g. 'Australia/Sydney'). Falls back to browser local if omitted.
 * @param {object} [options] - { showSeconds: false }
 * @returns {string} Formatted date/time string
 */
export function formatUserDateTime(timestamp, dateFormat = 'DD/MM/YYYY', timeFormat = '24h', userTimezone, options = {}) {
  if (!timestamp) return '-';
  // Ensure ambiguous timestamps (no Z or offset) are treated as UTC
  let ts = timestamp;
  if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) {
    ts = ts + 'Z';
  }
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '-';

  let day, month, year, hours, mins, secs;

  if (userTimezone) {
    // Use Intl to convert to the user's configured timezone
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }).formatToParts(d);
      const get = (type) => parts.find(p => p.type === type)?.value || '';
      day = get('day');
      month = get('month');
      year = get('year');
      hours = parseInt(get('hour'), 10);
      mins = get('minute');
      secs = get('second');
    } catch {
      // Invalid timezone — fall through to local
      day = String(d.getDate()).padStart(2, '0');
      month = String(d.getMonth() + 1).padStart(2, '0');
      year = d.getFullYear();
      hours = d.getHours();
      mins = String(d.getMinutes()).padStart(2, '0');
      secs = String(d.getSeconds()).padStart(2, '0');
    }
  } else {
    // Browser local timezone
    day = String(d.getDate()).padStart(2, '0');
    month = String(d.getMonth() + 1).padStart(2, '0');
    year = d.getFullYear();
    hours = d.getHours();
    mins = String(d.getMinutes()).padStart(2, '0');
    secs = String(d.getSeconds()).padStart(2, '0');
  }

  let datePart;
  if (dateFormat === 'MM/DD/YYYY') datePart = `${month}/${day}/${year}`;
  else if (dateFormat === 'YYYY-MM-DD') datePart = `${year}-${month}-${day}`;
  else datePart = `${day}/${month}/${year}`;

  let timePart;
  if (timeFormat === '12h') {
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h12 = hours % 12 || 12;
    timePart = options.showSeconds
      ? `${h12}:${mins}:${secs} ${ampm}`
      : `${h12}:${mins} ${ampm}`;
  } else {
    const hh = String(hours).padStart(2, '0');
    timePart = options.showSeconds
      ? `${hh}:${mins}:${secs}`
      : `${hh}:${mins}`;
  }

  return `${datePart} ${timePart}`;
}

/**
 * Fixed, unambiguous UTC stamp (ISO-style, 24h) for audit/compliance displays:
 * "2026-10-31 11:49 UTC". Independent of the user's date/time format on purpose.
 */
export function formatUTCStamp(timestamp, { seconds = false } = {}) {
  if (!timestamp) return '-';
  let ts = timestamp;
  if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) ts = ts + 'Z';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '-';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}${seconds ? ':' + p(d.getUTCSeconds()) : ''} UTC`;
}

/**
 * User-local timestamp with the exact UTC moment appended, for audit trails viewed
 * across timezones: "31/10/2026 22:49 (2026-10-31 11:49 UTC)".
 * When the user's timezone IS UTC, just appends "UTC" instead of repeating it.
 */
export function formatUserDateTimeWithUTC(timestamp, dateFormat = 'DD/MM/YYYY', timeFormat = '24h', userTimezone, options = {}) {
  const local = formatUserDateTime(timestamp, dateFormat, timeFormat, userTimezone, options);
  if (local === '-') return local;
  if (userTimezone === 'UTC' || userTimezone === 'Etc/UTC') return `${local} UTC`;
  return `${local} (${formatUTCStamp(timestamp)})`;
}
